import { z } from 'zod/v4';

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
export const MIN_ZXCVBN_SCORE = 3;
const SPECIAL_CHAR_REGEX = /[^a-zA-Z0-9]/;

// Must match user-service PasswordValidatorService, which returns the same copy on a 400.
export const PASSWORD_MISSING_REQUIREMENTS_MESSAGE =
    'Invalid password: Passwords must be at least 12 characters long and include 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.';
export const PASSWORD_NOT_COMPLEX_MESSAGE =
    'Your password is not complex enough. We recommend adding extra words, symbols, or numbers to add complexity and then try again.';

export const PASSWORD_REQUIREMENTS: readonly string[] = [
    `At least ${PASSWORD_MIN_LENGTH} or more characters`,
    '1 Lowercase character',
    '1 Uppercase character',
    '1 Number',
    '1 Special Character: ^ $ * . [ ] { } ( ) ? " ! @ # % & / \\ , > < \' : ; | _ ~ `',
];

export interface PasswordValidationResult {
    isValid: boolean;
    errors: string[];
}

export type PasswordScorer = (password: string) => number;

export function validatePassword(password: string): PasswordValidationResult {
    const errors: string[] = [];

    if (password.length < PASSWORD_MIN_LENGTH) {
        errors.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
    }
    if (password.length > PASSWORD_MAX_LENGTH) {
        errors.push(`Password must be at most ${PASSWORD_MAX_LENGTH} characters.`);
    }
    if (!/[a-z]/.test(password)) {
        errors.push('Password must contain a lowercase letter.');
    }
    if (!/[A-Z]/.test(password)) {
        errors.push('Password must contain an uppercase letter.');
    }
    if (!/[0-9]/.test(password)) {
        errors.push('Password must contain a number.');
    }
    if (!SPECIAL_CHAR_REGEX.test(password)) {
        errors.push('Password must contain a special character.');
    }

    return { isValid: errors.length === 0, errors };
}

export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`)
    .max(PASSWORD_MAX_LENGTH, `Password must be at most ${PASSWORD_MAX_LENGTH} characters.`)
    .regex(/[a-z]/, 'Password must contain a lowercase letter.')
    .regex(/[A-Z]/, 'Password must contain an uppercase letter.')
    .regex(/[0-9]/, 'Password must contain a number.')
    .regex(SPECIAL_CHAR_REGEX, 'Password must contain a special character.');

/**
 * `scorer` is null while the lazily loaded zxcvbn chunk is in flight. Only the baseline is checked
 * then, and user-service re-runs the full check.
 */
export function validatePasswordForSignUp(password: string, scorer: PasswordScorer | null): PasswordValidationResult {
    if (!validatePassword(password).isValid) {
        return { isValid: false, errors: [PASSWORD_MISSING_REQUIREMENTS_MESSAGE] };
    }
    if (scorer && scorer(password) < MIN_ZXCVBN_SCORE) {
        return { isValid: false, errors: [PASSWORD_NOT_COMPLEX_MESSAGE] };
    }
    return { isValid: true, errors: [] };
}
